<script>
import { mapGetters, mapActions } from 'vuex'
import { useAlert } from 'dashboard/composables'

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday']

export default {
  name: 'OperatingHours',
  props: {
    selectedBranch: {
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      days: DAYS,
      timeSlots: [],
      selectedDay: null,
      selectedWH: null,
      selectedDays: [],
      showErrorId: null,
      confirmDelete: false,
      confirmDeleteId: null,
      workingHours: [],
    }
  },
  computed: {
    ...mapGetters('operatingHours', ['all', 'isLoading', 'isSaving', 'error']),
    isOnboarding() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'setup_branch_operating_hours';
    },
    groupedWorkingHours() {
      const grouped = {}
      for (let day = 0; day <= 6; day++) grouped[day] = []

      // protect the computed from throwing if store contains invalid entries
      const validHours = Array.isArray(this.workingHours)
        ? this.workingHours.filter(e => e && e.hasOwnProperty('day_of_week'))
        : []

      const sorted = [...validHours].sort((a, b) => {
        const aDay = Number(a.day_of_week)
        const bDay = Number(b.day_of_week)
        const dayDiff = aDay - bDay
        const startCompare = String(a.start_time || '').localeCompare(String(b.start_time || ''))
        return dayDiff || startCompare
      })

      sorted.forEach(entry => {
        const dayIndex = Number(entry.day_of_week)
        if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) {
          // defensive: ignore invalid day_of_week but log for debugging
          // eslint-disable-next-line no-console
          console.warn('[OperatingHours] ignoring invalid day_of_week:', entry)
          return
        }

        grouped[dayIndex].push({
          ...entry,
          start_time: this.formatTime(entry.start_time),
          end_time: this.formatTime(entry.end_time),
        })
      })

      return grouped
    },
    filteredDays() {
      return this.days.filter(day => day !== this.selectedDay)
    }
  },
  watch: {
    selectedBranch: {
      immediate: true,
      handler() {
        if (this.selectedBranch) this.fetchHours()
      }
    }
  },
  methods: {
    ...mapActions('operatingHours', ['fetchOperatingHours', 'updateOperatingHours']),
    async fetchHours() {
      if (!this.selectedBranch) return
      try {
        const data = await this.fetchOperatingHours({
          accountId: this.selectedBranch.account_id,
          branchId: this.selectedBranch.branch_id
        });
        this.workingHours = data;
      } catch (err) {
        useAlert('Failed to fetch operating hours')
        console.error(err)
      } finally {
        this.generateTimeSlots()
      }
    },
    // Returns true when the server accepted the change. The list is replaced with the
    // server's copy so new slots get their real ids; a rejected change is dropped by
    // reloading, otherwise it would be sent again with the next save.
    async saveHours() {
      if (!this.selectedBranch) return false
      try {
        const saved = await this.updateOperatingHours({
          accountId: this.selectedBranch.account_id,
          branchId: this.selectedBranch.branch_id,
          payload: { 'hours': this.workingHours }
        })
        this.workingHours = Array.isArray(saved) ? saved : this.workingHours
        this.$store.dispatch('onboarding/completeLocalTask', 'setup_branch_operating_hours');
        return true
      } catch (err) {
        useAlert('Failed to save operating hours')
        console.error(err)
        await this.fetchHours()
        return false
      }
    },
    generateTimeSlots() {
      const slots = []
      for (let minutes = 0; minutes < 24*60; minutes += 15) {
        const hours = Math.floor(minutes/60)
        const mins = minutes % 60
        const ampm = hours >= 12 ? 'PM' : 'AM'
        const formattedHour = hours % 12 || 12
        slots.push(`${formattedHour}:${String(mins).padStart(2,'0')} ${ampm}`)
      }
      this.timeSlots = slots
    },
    formatTime(time) {
      if (!time) return ''
      const [hour, minute] = time.split(':')
      const h = parseInt(hour, 10)
      const suffix = h >= 12 ? 'PM' : 'AM'
      const formattedHour = h % 12 || 12
      return `${formattedHour}:${minute} ${suffix}`
    },
    parseTime(time) {
      if (!time) return '00:00:00'
      let [timePart, modifier] = time.split(' ')
      let [hours, minutes] = timePart.split(':')
      hours = parseInt(hours,10)
      if (modifier === 'AM' && hours === 12) hours = 0
      else if (modifier === 'PM' && hours !== 12) hours += 12
      return `${String(hours).padStart(2,'0')}:${minutes}:00`
    },
    incrementTime(time) {
      const [hour, minute, second] = (time || '00:00:00').split(':').map(Number)
      let newHour = hour + 1
      if (newHour === 24) newHour = 0
      return `${String(newHour).padStart(2,'0')}:${String(minute).padStart(2,'0')}:${String(second).padStart(2,'0')}`
    },
    // Times arrive as "h:mm AM" (display) or "HH:MM[:SS]" (server); compare them as "HH:MM:SS"
    normalizeTime(time) {
      if (!time) return ''
      if (/(AM|PM)$/i.test(time.trim())) return this.parseTime(time.trim().toUpperCase())
      const [hours = '00', minutes = '00', seconds = '00'] = time.split(':')
      return `${hours.padStart(2, '0')}:${minutes}:${seconds}`
    },
    isDuplicate({ oldTime, newTime }) {
      const start = this.normalizeTime(newTime.start_time)
      const end = this.normalizeTime(newTime.end_time)
      return oldTime.some(
        slot => start < this.normalizeTime(slot.end_time) && end > this.normalizeTime(slot.start_time)
      )
    },
    hasOverlapOnDay(dayIndex, startTime, endTime, ignoreId = null) {
      const sameDay = this.workingHours.filter(
        h => Number(h.day_of_week) === dayIndex && (ignoreId === null || h.id !== ignoreId)
      )
      return this.isDuplicate({ oldTime: sameDay, newTime: { start_time: startTime, end_time: endTime } })
    },
    // The dropdowns edit a display copy of the list; a new array rebuilds those copies from the real data
    refreshList() {
      this.workingHours = [...this.workingHours]
    },
    async handleEditWorkingHours({ id, dayIndex, startTime, endTime }) {
      const day = Number(dayIndex)
      if (this.normalizeTime(startTime) >= this.normalizeTime(endTime)) {
        useAlert('End time must be after start time')
        this.refreshList()
        return
      }
      if (this.hasOverlapOnDay(day, startTime, endTime, id)) {
        useAlert('Time slots cannot overlap')
        this.showErrorId = id
        this.refreshList()
        return
      }
      if (!this.workingHours.some(h => h.id === id)) return
      this.workingHours = this.workingHours.map(h =>
        h.id === id ? { ...h, day_of_week: day, start_time: startTime, end_time: endTime } : h
      )
      this.showErrorId = null
      if (await this.saveHours()) useAlert('Time slot updated')
    },
    async handleCreateWorkingHours({ dayIndex, previousEndTime }) {
      const day = Number(dayIndex)
      const previousEnd = this.parseTime(previousEndTime)
      // the new slot is one hour long and starts an hour later, so it must still end before midnight
      if (Number(previousEnd.split(':')[0]) > 21) {
        useAlert('No room left on this day for another time slot')
        return
      }
      const newStartTime = this.incrementTime(previousEnd)
      const newEndTime = this.incrementTime(newStartTime)
      this.workingHours.push({ day_of_week: day, start_time: newStartTime, end_time: newEndTime })
      if (await this.saveHours()) useAlert('Time slot added')
    },
    async handleCreateNewWorkingHours(dayIndex) {
      this.workingHours.push({ day_of_week: Number(dayIndex), start_time: '09:00:00', end_time: '17:00:00' })
      if (await this.saveHours()) useAlert('Time slot added')
    },
    async handleDeleteWorkingHours(id) {
      this.confirmDelete = false
      this.confirmDeleteId = null
      this.workingHours = this.workingHours.filter(h => h.id !== id)
      if (await this.saveHours()) useAlert('Time slot deleted')
    },
    async handleCopyWorkingHours(hours) {
      let skipped = 0
      const copied = this.selectedDays.flatMap(selectedDay => {
        const dayIndex = this.days.indexOf(selectedDay)
        return hours.flatMap(hour => {
          const start = this.parseTime(hour.start_time)
          const end = this.parseTime(hour.end_time)
          if (this.hasOverlapOnDay(dayIndex, start, end)) {
            skipped += 1
            return []
          }
          return [{ id: Date.now() + Math.random(), day_of_week: dayIndex, start_time: start, end_time: end }]
        })
      })
      this.closeDropdown()
      if (skipped) useAlert(`${skipped} time slot(s) overlap existing hours and were not copied`)
      if (!copied.length) return
      this.workingHours = [...this.workingHours, ...copied]
      if (await this.saveHours()) useAlert('Time slots copied')
    },
    toggleDayDropdown({ day, id }) {
      this.selectedDay = this.days[day]
      this.selectedWH = id
      this.$nextTick(() => setTimeout(() => {
        const dropdown = this.$refs.dropdownMenu
        if (dropdown && dropdown.scrollIntoView) dropdown.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 100))
    },
    closeDropdown() {
      this.selectedDay = null
      this.selectedWH = null
      this.selectedDays = []
    },
    openDeleteConfirmation(id) {
      this.confirmDelete = true;
      this.confirmDeleteId = id;
    },
    cancelDeleteConfirmation() {
      this.confirmDelete = false;
      this.confirmDeleteId = null;
    },

  },
  mounted() {
    this.generateTimeSlots()
  }
}
</script>

<template>
  <div class="mt-8 flex flex-col p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
      Operating Hours
    </h2>

    <div class="flex flex-col gap-2 p-2 flex-1">
      <div v-for="(hours, day) in groupedWorkingHours" :key="day" class="border-b border-slate-300 dark:border-slate-700 last:border-0">
        <h3 class="text-slate-800 dark:text-slate-200 flex items-center gap-2">
          {{ days[day] }}
        </h3>

        <ul v-if="hours.length > 0" class="space-y-4 mt-2">
          <li v-for="(hour, index) in hours" :key="hour.id" class="flex flex-wrap items-center gap-3 p-3 sm:p-4">
            <!-- Time Inputs -->
            <div class="flex justify-between items-center gap-2 w-96 max-w-96 sm:w-auto">
              <div class="w-full max-w-[200px]">
                <multiselect 
                  v-model="hour.start_time"
                  :options="timeSlots"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :multiple="false"
                  :class="['w-full text-slate-900 dark:text-white dark:bg-slate-700']"
                  placeholder="Start Time"
                  @input="handleEditWorkingHours({ id: hour.id, dayIndex: day, startTime: parseTime(hour.start_time), endTime: parseTime(hour.end_time) })"
                />
              </div>

              <span class="text-slate-700 dark:text-slate-300 px-1 mb-4">–</span>

              <div class="w-full max-w-[200px]">
                <multiselect 
                  v-model="hour.end_time"
                  :options="timeSlots"
                  :searchable="true"
                  :close-on-select="true"
                  :show-labels="false"
                  :multiple="false"
                  :class="['w-full text-slate-900 dark:text-white dark:bg-slate-700']"
                  placeholder="End Time"
                  @input="handleEditWorkingHours({ id: hour.id, dayIndex: day, startTime: parseTime(hour.start_time), endTime: parseTime(hour.end_time) })"
                />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-2 mt-3 sm:mt-0 mb-4">
              <button
                @click="handleCreateWorkingHours({ dayIndex: day, previousEndTime: hours[hours.length - 1].end_time })"
                class="p-2 text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-opacity-50"
                aria-label="Add Time Slot"
                title="Add Time Slot"
                :class="{ 'invisible': index !== 0 }"
              >
                <fluent-icon icon="add-circle" class="h-5 w-5" />
              </button>

              <!-- Copy Button with Dropdown -->
              <div class="relative" :class="{ 'invisible': index !== 0 }">
                <button 
                  @click="toggleDayDropdown({ day: day, id: hour.id })"
                  class="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-50"
                  aria-label="Copy to Days"
                  title="Copy to Days"
                >
                  <fluent-icon icon="copy" class="h-5 w-5" />
                </button>

                <div v-if="selectedDay === days[day] && selectedWH === hour.id" 
                  class="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40"
                  @click="closeDropdown"
                  aria-hidden="true"
                ></div>

                <div v-if="selectedDay === days[day] && selectedWH === hour.id" 
                  ref="dropdownMenu"
                  class="absolute right-0 sm:right-auto sm:left-0 mt-2 w-64 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 shadow-xl rounded-lg p-4 z-50 transition-all duration-200 origin-top-right"
                  role="dialog"
                  aria-labelledby="copy-dialog-title"
                >
                  <h4 id="copy-dialog-title" class="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
                    Copy To
                  </h4>

                  <div class="flex flex-col space-y-2 max-h-48 overflow-y-auto mb-2 border border-slate-200 dark:border-slate-600 rounded-lg p-2">
                    <label v-for="(label, idx) in filteredDays" :key="idx"
                      class="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors cursor-pointer"
                    >
                      <input 
                        type="checkbox" 
                        :value="label" 
                        v-model="selectedDays" 
                        class="w-4 h-4 text-woot-500 focus:ring-woot-500 rounded border-slate-300 dark:border-slate-600 dark:bg-slate-800"
                        :id="`day-${idx}`"
                      />
                      <span class="text-slate-800 dark:text-slate-300" :for="`day-${idx}`">{{ label }}</span>
                    </label>
                  </div>

                  <div class="flex justify-end gap-2">
                    <button 
                      @click="closeDropdown"
                      class="px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-opacity-50"
                    >
                      Cancel
                    </button>
                    <button 
                      @click="handleCopyWorkingHours(hours)"
                      class="px-3 py-1.5 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
                      :disabled="selectedDays.length === 0"
                      :class="{ 'opacity-50 cursor-not-allowed': selectedDays.length === 0 }"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>

              <button 
                @click="openDeleteConfirmation(hour.id)"
                v-if="!confirmDelete || confirmDeleteId !== hour.id"
                class="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                aria-label="Delete Time Slot"
                title="Delete Time Slot" 
              >
                <fluent-icon icon="delete" class="h-5 w-5" />
              </button>

              <!-- Delete confirmation inline -->
              <div v-if="confirmDelete && confirmDeleteId === hour.id" class="flex gap-2 animate-fadeIn">
                <button 
                  @click="cancelDeleteConfirmation"
                  class="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                  aria-label="Cancel"
                >
                  <fluent-icon icon="dismiss" class="h-5 w-5" />
                </button>
                <button 
                  @click="handleDeleteWorkingHours(confirmDeleteId)"
                  class="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  aria-label="Apply"
                >
                  <fluent-icon icon="checkmark" class="h-5 w-5" />
                </button>
              </div>
            </div>

            <!-- Error message -->
            <div v-if="showErrorId === hour.id"
              class="w-full mt-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-2 rounded-lg flex items-center gap-2 animate-fadeIn">
              <fluent-icon icon="warning" class="h-4 w-4 flex-shrink-0" />
              <p class="text-sm">
                Time slots cannot overlap
              </p>
            </div>
          </li>
        </ul>

        <div v-else class="w-96 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg my-2 border border-dashed border-slate-300 dark:border-slate-700">
          <p class="text-slate-500 dark:text-slate-400 italic">
            No hours set
          </p>
          <div class="relative inline-block">
            <button 
              @click="handleCreateNewWorkingHours(day)"
              class="p-2 text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300 rounded-lg hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors focus:outline-none focus:ring-2 focus:ring-woot-500 focus:ring-opacity-50"
              aria-label="Add Hours"
              title="Add Hours"
            >
              <fluent-icon icon="add-circle" class="h-5 w-5" />
            </button>
            <!-- Onboarding Hint Tooltip -->
            <div v-if="isOnboarding && day == 0" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
              <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
                Hint: Click to create your operating hours <fluent-icon icon="caret-up" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-in-out;
}
</style>